import { DesignObject } from '../types/interfaces';

/** Konva findOne('#id') uses CSS selectors — IDs must not be bare numbers. */
export function isKonvaSafeId(id: string): boolean {
  return /^[A-Za-z_][\w-]*$/.test(String(id || ''));
}

export function makeDesignObjectId(now = Date.now(), rand = Math.random()): string {
  const suffix = Math.floor(rand * 1e6)
    .toString(36)
    .padStart(4, '0');
  return `obj-${now}-${suffix}`;
}

/** Rewrite legacy numeric/unsafe ids so selection/transformer keep working after reload. */
export function normalizeDesignObjectId(id: string, index = 0): string {
  const raw = String(id || '').trim();
  if (isKonvaSafeId(raw)) return raw;
  const safe = raw.replace(/[^A-Za-z0-9_-]/g, '') || 'legacy';
  return `obj-${safe || index}-${index}`;
}

export function normalizeDesignObjects(objects: DesignObject[]): DesignObject[] {
  const seen = new Set<string>();
  return (objects || []).map((obj, index) => {
    let id = normalizeDesignObjectId(obj.id, index);
    while (seen.has(id)) {
      id = `${id}-${index}-${seen.size}`;
    }
    seen.add(id);
    return { ...obj, id };
  });
}

/** Cascade so newly added shapes are not stacked on the previous one. */
export function nextObjectPosition(
  existingCount: number,
  base = { x: 60, y: 60 },
  step = 36
): { x: number; y: number } {
  const col = existingCount % 8;
  const row = Math.floor(existingCount / 8);
  return {
    x: base.x + col * step,
    y: base.y + row * step,
  };
}

/** Smallest shape edge (px) the designer allows; circles = diameter. */
export const MIN_SHAPE_SIZE = 20;
export const DEFAULT_TANK_RADIUS = 30;
export const DEFAULT_BARREL_RADIUS = 16;

/**
 * Turn a Konva node's transform (scale) into stored size. Read the node
 * synchronously: callers reset scale to 1 right after, so this must not run
 * inside a deferred React state updater.
 */
export function sizeFromTransform(
  shape: 'circle' | 'rectangle',
  node: { x: number; y: number; radius?: number; width?: number; height?: number; scaleX: number; scaleY: number }
): Partial<DesignObject> {
  const round = (n: number) => Math.round(n * 10) / 10;
  if (shape === 'circle') {
    // Keep aspect: use the larger scale so a corner drag behaves like the box.
    const s = Math.max(Math.abs(node.scaleX), Math.abs(node.scaleY));
    const radius = Math.max(MIN_SHAPE_SIZE / 2, (node.radius || DEFAULT_TANK_RADIUS) * s);
    return { x: round(node.x), y: round(node.y), radius: round(radius) };
  }
  return {
    x: round(node.x),
    y: round(node.y),
    width: round(Math.max(MIN_SHAPE_SIZE, (node.width || 100) * Math.abs(node.scaleX))),
    height: round(Math.max(MIN_SHAPE_SIZE, (node.height || 60) * Math.abs(node.scaleY))),
  };
}

export function createDesignObject(
  type: 'Tank' | 'Barrel' | 'Storage',
  existingCount: number,
  idFactory: () => string = makeDesignObjectId
): DesignObject {
  const pos = nextObjectPosition(existingCount);
  return {
    id: idFactory(),
    type,
    shape: type === 'Storage' ? 'rectangle' : 'circle',
    x: pos.x,
    y: pos.y,
    width: type === 'Storage' ? 100 : undefined,
    height: type === 'Storage' ? 60 : undefined,
    radius: type === 'Tank' ? DEFAULT_TANK_RADIUS : type === 'Barrel' ? DEFAULT_BARREL_RADIUS : undefined,
    name: '',
    abbreviation: type === 'Barrel' ? 'BBL' : '',
  };
}

export function updateObjectById(
  objects: DesignObject[],
  id: string,
  patch: Partial<DesignObject>
): DesignObject[] {
  return objects.map((obj) => (obj.id === id ? { ...obj, ...patch } : obj));
}

export function removeObjectById(objects: DesignObject[], id: string): DesignObject[] {
  return objects.filter((obj) => obj.id !== id);
}

export function moveObjectToEnd(objects: DesignObject[], id: string): DesignObject[] {
  const idx = objects.findIndex((o) => o.id === id);
  if (idx < 0) return objects;
  const next = [...objects];
  const [item] = next.splice(idx, 1);
  next.push(item);
  return next;
}

export function designNeedsAssignment(objects: DesignObject[]): boolean {
  return objects.some((obj) => !obj.locationId && !obj.equipmentId);
}

export function designNeedsLabels(objects: DesignObject[]): boolean {
  return objects.some((obj) => !obj.name || !obj.abbreviation);
}

/* ---------- Multi-select align / distribute ---------- */

export type AlignMode = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
export type DistributeAxis = 'horizontal' | 'vertical';

export interface ShapeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Edge box of a shape: circles are positioned by center, rectangles by top-left. */
export function shapeBounds(o: DesignObject): ShapeBounds {
  if (o.shape === 'circle') {
    const r = o.radius || DEFAULT_TANK_RADIUS;
    return { left: o.x - r, top: o.y - r, right: o.x + r, bottom: o.y + r };
  }
  const w = o.width || 100;
  const h = o.height || 60;
  return { left: o.x, top: o.y, right: o.x + w, bottom: o.y + h };
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Move a shape so its left/top edge lands at the given value. */
function placeEdge(o: DesignObject, axis: 'x' | 'y', edge: number): DesignObject {
  const b = shapeBounds(o);
  const delta = axis === 'x' ? edge - b.left : edge - b.top;
  return axis === 'x' ? { ...o, x: round1(o.x + delta) } : { ...o, y: round1(o.y + delta) };
}

/** Align the selected shapes (2+) to the selection's bounding box edge/center. */
export function alignObjects(objects: DesignObject[], ids: string[], mode: AlignMode): DesignObject[] {
  const sel = objects.filter((o) => ids.includes(o.id));
  if (sel.length < 2) return objects;
  const bs = sel.map(shapeBounds);
  const minL = Math.min(...bs.map((b) => b.left));
  const maxR = Math.max(...bs.map((b) => b.right));
  const minT = Math.min(...bs.map((b) => b.top));
  const maxB = Math.max(...bs.map((b) => b.bottom));
  return objects.map((o) => {
    if (!ids.includes(o.id)) return o;
    const b = shapeBounds(o);
    const w = b.right - b.left;
    const h = b.bottom - b.top;
    switch (mode) {
      case 'left':
        return placeEdge(o, 'x', minL);
      case 'right':
        return placeEdge(o, 'x', maxR - w);
      case 'center':
        return placeEdge(o, 'x', (minL + maxR) / 2 - w / 2);
      case 'top':
        return placeEdge(o, 'y', minT);
      case 'bottom':
        return placeEdge(o, 'y', maxB - h);
      case 'middle':
        return placeEdge(o, 'y', (minT + maxB) / 2 - h / 2);
      default:
        return o;
    }
  });
}

/**
 * Distribute 3+ shapes so the gaps between edges are equal (mixed sizes look
 * even). The outermost two stay put; the rest are placed in order between them.
 */
export function distributeObjects(objects: DesignObject[], ids: string[], axis: DistributeAxis): DesignObject[] {
  const sel = objects.filter((o) => ids.includes(o.id));
  if (sel.length < 3) return objects;
  const lo = (o: DesignObject) => (axis === 'horizontal' ? shapeBounds(o).left : shapeBounds(o).top);
  const size = (o: DesignObject) => {
    const b = shapeBounds(o);
    return axis === 'horizontal' ? b.right - b.left : b.bottom - b.top;
  };
  const sorted = [...sel].sort((a, b) => lo(a) - lo(b) || a.id.localeCompare(b.id));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const span = lo(last) + size(last) - lo(first);
  const total = sorted.reduce((s, o) => s + size(o), 0);
  const gap = (span - total) / (sorted.length - 1);
  const placed = new Map<string, DesignObject>();
  let cursor = lo(first);
  for (const o of sorted) {
    placed.set(o.id, placeEdge(o, axis === 'horizontal' ? 'x' : 'y', cursor));
    cursor += size(o) + gap;
  }
  return objects.map((o) => placed.get(o.id) || o);
}

/** Shift several shapes by the same delta (group drag). */
export function moveObjects(objects: DesignObject[], ids: string[], dx: number, dy: number): DesignObject[] {
  if (!dx && !dy) return objects;
  return objects.map((o) => (ids.includes(o.id) ? { ...o, x: round1(o.x + dx), y: round1(o.y + dy) } : o));
}

/** Ids of shapes whose edge box intersects a drag-select rectangle. */
export function idsInBox(objects: DesignObject[], box: ShapeBounds): string[] {
  const l = Math.min(box.left, box.right);
  const r = Math.max(box.left, box.right);
  const t = Math.min(box.top, box.bottom);
  const btm = Math.max(box.top, box.bottom);
  return objects
    .filter((o) => {
      const b = shapeBounds(o);
      return b.right >= l && b.left <= r && b.bottom >= t && b.top <= btm;
    })
    .map((o) => o.id);
}

/** Shift/ctrl/cmd-click (or "Select multiple" mode) toggles; plain click selects one. */
export function nextSelection(current: string[], id: string, additive: boolean): string[] {
  if (!additive) return [id];
  return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
}

/**
 * Selection is decided on pointer DOWN, because a real click often moves the
 * mouse a few pixels; Konva then treats it as a drag and never fires "click",
 * so a click-only handler dropped shift-clicks. Down on an unselected shape
 * selects it (adds with a modifier). Down on an already-selected shape keeps
 * the selection so the group can be dragged; the change (remove with a modifier,
 * or narrow to that one shape) waits for a click with no drag in between.
 */
export interface PendingSelect {
  id: string;
  additive: boolean;
}
export function selectionOnPointerDown(
  current: string[],
  id: string,
  additive: boolean
): { next: string[]; pending: PendingSelect | null } {
  if (!current.includes(id)) return { next: additive ? [...current, id] : [id], pending: null };
  return { next: current, pending: { id, additive } };
}
export function selectionOnClick(current: string[], pending: PendingSelect | null, id: string): string[] {
  if (!pending || pending.id !== id) return current;
  if (pending.additive) return current.filter((x) => x !== id);
  return current.length > 1 ? [id] : current;
}

/**
 * Empty-canvas tap detection (phones). A touch that starts on the empty stage is
 * usually the start of a page scroll, so it must NOT deselect. Only a short,
 * nearly-still tap (no page scroll in between) counts as "tap empty canvas".
 */
export const TAP_SLOP_PX = 10;
export const TAP_MAX_MS = 700;

export interface TapGesture {
  x: number;
  y: number;
  t: number;
  scroll: number;
  moved: boolean;
}

export function tapStart(x: number, y: number, t: number, scroll = 0): TapGesture {
  return { x, y, t, scroll, moved: false };
}

export function tapMove(g: TapGesture | null, x: number, y: number, slop = TAP_SLOP_PX): TapGesture | null {
  if (!g || g.moved) return g;
  if (Math.hypot(x - g.x, y - g.y) > slop) return { ...g, moved: true };
  return g;
}

export function isDeliberateTap(
  g: TapGesture | null,
  x: number,
  y: number,
  t: number,
  scroll = 0,
  slop = TAP_SLOP_PX
): boolean {
  if (!g || g.moved) return false;
  if (Math.hypot(x - g.x, y - g.y) > slop) return false;
  if (Math.abs(scroll - g.scroll) > 2) return false;
  if (t - g.t > TAP_MAX_MS) return false;
  return true;
}
